/**
 * Camera + calibration primitives for the browser capture client.
 *
 * Ported from baseball-card-capture's app.js, with the DOM stripped out: these
 * are functions over a <video> or <canvas>, and the React screen owns the UI.
 *
 * Two calibrations, and they are NOT symmetric — the distinction is the thing
 * to keep straight:
 *
 *   • Colour (white balance) is APPLIED to the pixels before the JPEG is
 *     encoded, so the bytes we store are already corrected.
 *   • Background ("tare") is pure METADATA. It is never applied to the image;
 *     it rides along in capture_meta so the cropper can chroma-key the mat away
 *     years later, under whatever lighting the photo was actually taken in.
 *
 * Both are session-only by design (no localStorage). A stale calibration that
 * silently survives a change of mat or lighting is worse than none.
 */

export interface ColorCalibration {
  gains: { r: number; g: number; b: number };
  sample: { r: number; g: number; b: number };
  swatch: string;
  target: number;
  updatedAt: string;
}

export interface BackgroundCalibration {
  /** OpenCV-style hue windows, 0..179, split in two when they wrap. */
  hRanges: [number, number][];
  sMin: number;
  vMin: number;
  peakH: number;
  swatch: string;
  updatedAt: string;
}

export interface FocusSupport {
  track: MediaStreamTrack | null;
  singleShot: boolean;
  continuous: boolean;
}

export interface CapturedFrame {
  blob: Blob;
  width: number;
  height: number;
}

// ── Cameras ──────────────────────────────────────────────────────────────────

/**
 * Enumerate video inputs. Labels are only populated once permission has been
 * granted, so this asks for a throwaway stream first and stops it immediately —
 * without that, every camera reads as "Camera 2" and a studio with three of
 * them is unusable.
 */
export async function listCameras(): Promise<MediaDeviceInfo[]> {
  const probe = await navigator.mediaDevices.getUserMedia({ video: true });
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === 'videoinput');
  } finally {
    probe.getTracks().forEach((t) => t.stop());
  }
}

/**
 * Open one camera at the highest resolution it will give us. `ideal` rather
 * than `exact`: a phone that cannot do 4K should fall back, not fail.
 *
 * `facingMode` is the mobile path — a rear camera is asked for by facing, not
 * by device id, because ids are not stable across sessions on iOS.
 */
export async function startStream(opts: {
  deviceId?: string;
  facingMode?: 'environment' | 'user';
}): Promise<MediaStream> {
  const video: MediaTrackConstraints = {
    width: { ideal: 4096 },
    height: { ideal: 2160 },
  };
  if (opts.deviceId) video.deviceId = { exact: opts.deviceId };
  else if (opts.facingMode) video.facingMode = { ideal: opts.facingMode };

  return navigator.mediaDevices.getUserMedia({ video, audio: false });
}

export function detectFocusCapabilities(track: MediaStreamTrack): FocusSupport {
  const caps: any = track.getCapabilities ? track.getCapabilities() : {};
  const modes: string[] = caps.focusMode || [];
  const support: FocusSupport = {
    track,
    singleShot: modes.includes('single-shot'),
    continuous: modes.includes('continuous'),
  };
  if (support.continuous) {
    // Continuous AF is the right resting state for a copy stand: the operator
    // swaps cards constantly and should not have to think about focus.
    track.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] }).catch(() => {});
  }
  return support;
}

/** Ask for a single-shot refocus and give the lens time to settle. */
export async function triggerAutofocus(support: FocusSupport, wait = 500): Promise<boolean> {
  if (!support.track || !support.singleShot) return false;
  try {
    await support.track.applyConstraints({ advanced: [{ focusMode: 'single-shot' } as any] });
    await new Promise((r) => setTimeout(r, wait));
    return true;
  } catch {
    return false;
  }
}

// ── Colour ───────────────────────────────────────────────────────────────────

/**
 * One sRGB byte triple to OpenCV-style HSV (H 0..179, S/V 0..255).
 *
 * Matching OpenCV's scale rather than the web's 0..360 is deliberate: the
 * profile this feeds is consumed by cv.inRange in the cropper, and a profile
 * that needs rescaling at the point of use is a profile that will eventually
 * be used unscaled.
 */
export function rgbToHsvBytes(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const v = max;
  const span = max - min;
  const s = max === 0 ? 0 : Math.round((span / max) * 255);
  let h = 0;
  if (span !== 0) {
    if (max === r) h = ((g - b) / span) % 6;
    else if (max === g) h = (b - r) / span + 2;
    else h = (r - g) / span + 4;
    h *= 30; // 60° / 2, to land on OpenCV's 0..179
    if (h < 0) h += 180;
  }
  return [Math.round(h) % 180, s, v];
}

/** In-place per-channel gain. One pass over the pixel array. */
export function applyColorCalibration(canvas: HTMLCanvasElement, cal: ColorCalibration) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = img.data;
  const { r: gr, g: gg, b: gb } = cal.gains;
  for (let i = 0; i < d.length; i += 4) {
    const r = d[i] * gr;
    const g = d[i + 1] * gg;
    const b = d[i + 2] * gb;
    d[i] = r > 255 ? 255 : r < 0 ? 0 : r;
    d[i + 1] = g > 255 ? 255 : g < 0 ? 0 : g;
    d[i + 2] = b > 255 ? 255 : b < 0 ? 0 : b;
  }
  ctx.putImageData(img, 0, 0);
}

/**
 * Build a white-balance profile from a patch the user clicked on a colour
 * chart. Anchors on green and floors each channel, so clicking something
 * near-black produces a mild correction rather than runaway gains.
 */
export function sampleNeutralGray(
  canvas: HTMLCanvasElement,
  x: number,
  y: number
): { calibration: ColorCalibration; looksNeutral: boolean } | null {
  const SAMPLE = 21; // odd, so there is a true centre pixel
  const half = (SAMPLE - 1) / 2;
  const x0 = Math.max(0, x - half);
  const y0 = Math.max(0, y - half);
  const w = Math.min(SAMPLE, canvas.width - x0);
  const h = Math.min(SAMPLE, canvas.height - y0);
  if (w <= 0 || h <= 0) return null;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const data = ctx.getImageData(x0, y0, w, h).data;

  let rs = 0;
  let gs = 0;
  let bs = 0;
  let n = 0;
  for (let i = 0; i < data.length; i += 4) {
    rs += data[i];
    gs += data[i + 1];
    bs += data[i + 2];
    n++;
  }
  const r = rs / n;
  const g = gs / n;
  const b = bs / n;
  const safe = (v: number) => Math.max(8, v);
  const target = g;

  const maxRatio = Math.max(r, g, b) / Math.max(1, Math.min(r, g, b));

  return {
    looksNeutral: maxRatio <= 1.6,
    calibration: {
      gains: { r: target / safe(r), g: 1, b: target / safe(b) },
      sample: { r: Math.round(r), g: Math.round(g), b: Math.round(b) },
      swatch: `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`,
      target: Math.round(target),
      updatedAt: new Date().toISOString(),
    },
  };
}

// ── Background ("tare") ──────────────────────────────────────────────────────

/**
 * Sample the empty mat and derive an HSV window that isolates it.
 *
 * Downsampled to 640px wide first: hue and S/V distributions are robust to box
 * sampling at this scale, and walking every pixel of a 4K frame to reach the
 * same answer costs a visible pause.
 *
 * Returns null when the mat is too desaturated to chroma-key — a gray mat has
 * no hue to key on, and pretending otherwise produces a profile that quietly
 * matches the card as well as the background.
 */
export function buildBackgroundProfile(video: HTMLVideoElement): BackgroundCalibration | null {
  if (!video.videoWidth || !video.videoHeight) return null;

  const targetW = Math.min(640, video.videoWidth);
  const scale = targetW / video.videoWidth;
  const w = targetW;
  const h = Math.round(video.videoHeight * scale);

  const cap = document.createElement('canvas');
  cap.width = w;
  cap.height = h;
  const ctx = cap.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  const { data } = ctx.getImageData(0, 0, w, h);

  // One pass: hue histogram plus running sums for the mean/variance of S and V.
  const hHist = new Uint32Array(180);
  let rSum = 0;
  let gSum = 0;
  let bSum = 0;
  let sSum = 0;
  let sSqSum = 0;
  let vSum = 0;
  let vSqSum = 0;
  let n = 0;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const [hh, ss, vv] = rgbToHsvBytes(r, g, b);
    hHist[hh]++;
    rSum += r;
    gSum += g;
    bSum += b;
    sSum += ss;
    sSqSum += ss * ss;
    vSum += vv;
    vSqSum += vv * vv;
    n++;
  }
  if (n === 0) return null;

  let peakH = 0;
  let peakCount = -1;
  for (let i = 0; i < 180; i++) {
    if (hHist[i] > peakCount) {
      peakCount = hHist[i];
      peakH = i;
    }
  }

  const meanS = sSum / n;
  const stdS = Math.sqrt(Math.max(0, sSqSum / n - meanS * meanS));
  const meanV = vSum / n;
  const stdV = Math.sqrt(Math.max(0, vSqSum / n - meanV * meanV));

  if (meanS < 40) return null; // too gray to key on

  // ±20° around the dominant hue, split when it wraps so a red mat near H=0
  // still produces a usable window.
  const halfWidth = 20;
  const lo = peakH - halfWidth;
  const hi = peakH + halfWidth;
  let hRanges: [number, number][];
  if (lo < 0)
    hRanges = [
      [0, hi],
      [180 + lo, 179],
    ];
  else if (hi > 179)
    hRanges = [
      [lo, 179],
      [0, hi - 180],
    ];
  else hRanges = [[lo, hi]];

  return {
    hRanges,
    sMin: Math.max(30, Math.round(meanS - stdS * 2.5)),
    vMin: Math.max(20, Math.round(meanV - stdV * 2.5)),
    peakH,
    swatch: `rgb(${Math.round(rSum / n)}, ${Math.round(gSum / n)}, ${Math.round(bSum / n)})`,
    updatedAt: new Date().toISOString(),
  };
}

// ── Capture ──────────────────────────────────────────────────────────────────

function canvasToBlob(canvas: HTMLCanvasElement, quality = 0.95): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Could not encode the frame'))),
      'image/jpeg',
      quality
    );
  });
}

/** Grab a full-resolution still, white-balanced before JPEG encoding. */
export async function captureFrame(
  video: HTMLVideoElement,
  color: ColorCalibration | null
): Promise<CapturedFrame> {
  if (!video.videoWidth || !video.videoHeight) throw new Error('Camera is not ready yet');

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);

  if (color) applyColorCalibration(canvas, color);

  return {
    blob: await canvasToBlob(canvas),
    width: canvas.width,
    height: canvas.height,
  };
}

/** Snapshot the live frame at full size, for the colour-chart click target. */
export function grabStill(video: HTMLVideoElement): HTMLCanvasElement | null {
  if (!video.videoWidth || !video.videoHeight) return null;
  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d')?.drawImage(video, 0, 0);
  return canvas;
}

/** What gets stamped onto every asset captured under the current calibration. */
export function buildCaptureMeta(
  bg: BackgroundCalibration | null,
  color: ColorCalibration | null
): Record<string, unknown> | null {
  if (!bg && !color) return null;
  return {
    capturedAt: new Date().toISOString(),
    source: 'browser-capture',
    bgCalibration: bg ? { ...bg } : null,
    colorCalibration: color ? { ...color } : null,
  };
}
